import React from 'react';
import * as Sentry from '@sentry/react';
import { getUnixTime } from 'date-fns';
import { proxy, useSnapshot } from 'valtio';
import { gql } from '@soundxyz/gql-string';
import type { ReportPlayStoppedInput } from '@graphql/reactQuery';
import { fetchGQL, ReportPlayStoppedAnalyticsDocument } from '@graphql/reactQuery';
import { getAnonymousID } from '../../lib/analytics';

export interface PlaySession {
  startTime: number;
  trackID: string;
}

interface UserSessionStats {
  numTimesPaused: number;
  lastStartTime?: number;
  listenDurationSeconds: number;
}

interface SealedSession extends PlaySession, UserSessionStats {
  endTime: number;
}

interface TrackPlaySessionState {
  sealedSession?: SealedSession;
  currentSession?: PlaySession;
  stats: UserSessionStats;
}

const trackPlaySessionsState = proxy<TrackPlaySessionState>({
  stats: {
    numTimesPaused: 0,
    listenDurationSeconds: 0,
  },
});

const reset = () => {
  trackPlaySessionsState.currentSession = undefined;
  trackPlaySessionsState.stats = {
    numTimesPaused: 0,
    listenDurationSeconds: 0,
    lastStartTime: undefined,
  };
};

const currentListenDuration = (): number => {
  if (!trackPlaySessionsState.stats.lastStartTime) return 0;

  return getUnixTime(Date.now() - trackPlaySessionsState.stats.lastStartTime);
};

const updateListenDuration = () => {
  if (trackPlaySessionsState.stats.lastStartTime) {
    trackPlaySessionsState.stats.listenDurationSeconds += currentListenDuration();
    trackPlaySessionsState.stats.lastStartTime = undefined;
  }
};

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation reportPlayStoppedAnalytics($input: ReportPlayStoppedInput!) {
    reportPlayStopped(input: $input)
  }
`);

export const SealedSessionReporter = React.memo(function SealedSessionReporter() {
  const { sealedSession } = useSnapshot(trackPlaySessionsState);

  React.useEffect(() => {
    const latestReport = sealedSession;
    if (!latestReport) return;

    const reportInput: ReportPlayStoppedInput = {
      uuid: getAnonymousID(),
      trackId: latestReport.trackID,
      start: latestReport.startTime,
      finish: latestReport.endTime,
      listenDuration: latestReport.listenDurationSeconds,
      pauseCount: latestReport.numTimesPaused,
    };
    fetchGQL(ReportPlayStoppedAnalyticsDocument, {
      variables: {
        input: reportInput,
      },
    }).catch(err => {
      Sentry.captureException(err);
    });
  }, [sealedSession]);

  return null;
});

export const getCurrentSession = () => {
  return trackPlaySessionsState.currentSession;
};

export const getLastFinishedSession = () => {
  return trackPlaySessionsState.sealedSession;
};

export const startSession = (session: PlaySession) => {
  trackPlaySessionsState.currentSession = session;
  trackPlaySessionsState.stats.lastStartTime = session.startTime;
};

export const userPaused = () => {
  if (!trackPlaySessionsState.currentSession) return;

  trackPlaySessionsState.stats.numTimesPaused++;
  updateListenDuration();
};

export const userResumedListening = () => {
  if (!trackPlaySessionsState.currentSession) return;

  trackPlaySessionsState.stats.lastStartTime = Date.now();
};

export const endActiveSession = () => {
  if (!trackPlaySessionsState.currentSession) return;
  updateListenDuration();

  const currentSession = trackPlaySessionsState.currentSession;

  const sealedSession: SealedSession = {
    ...currentSession,
    ...trackPlaySessionsState.stats,
    endTime: Date.now(),
  };

  trackPlaySessionsState.sealedSession = sealedSession;
  reset();
};
